import React, { useState, useRef, useEffect } from 'react';
import Header from './header';
import {
  Box,
  Text,
  Flex,
  Dialog,
  DialogContent,
  Heading,
  Button,
} from '@sparrowengg/twigs-react';
import SideBar from './sidebar';
import CodeBlock from './codeblock';
import IParams from './iparams';
import OAuth from './oauth';
import Settings from './settings';
import { EventsType, FieldType } from '../types';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { handleTooltipContent, setEventFields } from 'redux-store/actions';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { CustomTooltip } from './custom-tooltip';
import { dashboardFields, sidebarItems, tooltipContentValues } from '../constants';
import { ChevronRightIcon, RocketIcon } from '@sparrowengg/twigs-react-icons';
import LogOut from 'commons/icons/log-out';
import { useTourSteps } from '../hooks';
import { IRootDispatch, IRootState } from 'commons/types';
import { SHOW_TOUR, booleanStringValues } from 'commons/constants';
import { getLocalStorageValue, setLocalStorageValue } from 'commons/helper';

const CodeEditor = ({ events, currentFetchedEvent }: { events: EventsType[], currentFetchedEvent?: FieldType }) => {
  const [currentField, setCurrentField] = useState<FieldType>({
    type: null,
    field: null,
    code: null
  });
  const [showTour, setShowTour] = useState({
    modal: false,
    guide: false
  });
  const dispatch = useDispatch<IRootDispatch>();
  const eventFields = useSelector((state: IRootState) => state.eventFields);
  const [headerBtnLoader, setHeaderBtnLoader] = useState(false);
  // React Joyride Default States
  const [tourState, setTourState] = useState<{
    run: boolean;
    stepIndex: number;
    steps: any;
  }>({
    run: false,
    stepIndex: 0,
    steps: []
  });
  // Refs to target elements for the tour
  const eventHandlerRef = useRef<HTMLDivElement>(null);
  const codeBlockRef = useRef<HTMLDivElement>(null);
  const validateRef = useRef<HTMLButtonElement>(null);
  const consoleRef = useRef<HTMLDivElement>(null);
  const eventSimulationRef = useRef<HTMLDivElement>(null);
  const iparamsRef = useRef<HTMLDivElement>(null);
  const oauthRef = useRef<HTMLDivElement>(null);
  const publishAppRef = useRef<HTMLButtonElement>(null);
  const { createTourSteps } = useTourSteps({
    eventHandlerRef,
    codeBlockRef,
    validateRef,
    consoleRef,
    eventSimulationRef,
    iparamsRef,
    oauthRef,
    publishAppRef
  });

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    const stepContent: typeof data.step & { value?: string } = data?.step;
    if (ACTIONS.CLOSE === action) {
      setTourState((prev) => ({
        ...prev,
        run: false,
        stepIndex: 0
      }));
      setLocalStorageValue(SHOW_TOUR, booleanStringValues.FALSE);
    }
    if (
      stepContent?.value === tooltipContentValues.CODE_SNIPPET &&
      eventFields?.length < 1
    ) {
      handleSidebarItem(sidebarItems.EVENT_HANDLER, 'onSubmissionComplete');
      dispatch(handleTooltipContent('isEventOpen', true));
    }
    if (stepContent?.value === tooltipContentValues.IPARAMS) {
      dispatch(handleTooltipContent('isEventOpen', true));
    }
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(
        status as 'skipped' | 'finished'
      )
    ) {
      setTourState((prev) => ({
        ...prev,
        run: false,
        stepIndex: 0
      }));
      setLocalStorageValue(SHOW_TOUR, booleanStringValues.FALSE);
    } else if (
      [EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(
        type as 'step:after' | 'error:target_not_found'
      )
    ) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      setTourState((prev) => ({
        ...prev,
        stepIndex: nextStepIndex
      }));
    }
  };

  const handleCurrentField = (type: string, field: string) => {
    setCurrentField({
      type,
      field,
      code:
        eventFields.find((eventField) => eventField.field === field)?.code ??
        null
    });
  };

  const handleSidebarItem = (type: string, field: string) => {
    dispatch(
      setEventFields([
        ...eventFields,
        {
          type,
          field,
          code: `//${field} \n\n\n console.log('${field}: Event Handler'); \n console.log('ARGS', JSON.stringify(args));`
        }
      ])
    );
    handleCurrentField(type, field);
  };

  useEffect(() => {
    const isTourEnabled =
      getLocalStorageValue(SHOW_TOUR) !== booleanStringValues.FALSE;

    if (!isTourEnabled) {
      setShowTour({ modal: false, guide: false });
      setTourState((prev) => ({
        ...prev,
        run: false,
        stepIndex: 0
      }));
    } else {
      setShowTour({ modal: true, guide: false });
    }
    if (currentFetchedEvent) {
      setCurrentField(currentFetchedEvent);
    }
  }, []);

  return (
    <>
      {(showTour.guide && getLocalStorageValue(SHOW_TOUR) !== booleanStringValues.FALSE) && (
        <Joyride
          callback={handleJoyrideCallback}
          continuous
          run={tourState.run}
          stepIndex={tourState.stepIndex}
          steps={tourState.steps}
          hideCloseButton
          disableCloseOnEsc
          disableOverlayClose
          tooltipComponent={CustomTooltip}
        />
      )}

      <Header
        handleCurrentField={handleCurrentField}
        validateRef={validateRef}
        publishAppRef={publishAppRef}
        currentField={currentField}
        headerBtnLoader={headerBtnLoader}
      />
      <Flex alignItems="flex-start">
        <SideBar
          eventFields={eventFields}
          events={events}
          eventHandlerRef={eventHandlerRef}
          iparamsRef={iparamsRef}
          oauthRef={oauthRef}
          removeEventFields={(field) => {
            dispatch(
              setEventFields(
                eventFields.filter(
                  (currentField) => currentField.field !== field
                )
              )
            );
            if (currentField.field === field) {
              setCurrentField({
                type: null,
                field: null,
                code: null
              });
            }
          }}
          currentField={currentField}
          handleCurrentField={handleCurrentField}
          handleSidebarItem={handleSidebarItem}
        />
        {currentField.type === sidebarItems.CONFIGURATION ? (
          <>
            {currentField.field === dashboardFields.IPARAMS ? (
              <IParams />
            ) : (
              <OAuth />
            )}
          </>
        ) : currentField.type !== dashboardFields.SETTINGS ? (
          <CodeBlock
            codeBlockRef={codeBlockRef}
            currentField={currentField}
            events={events}
            eventFields={eventFields}
            consoleRef={consoleRef}
            eventSimulationRef={eventSimulationRef}
            handleHeaderBtnLoader={(value: boolean) => setHeaderBtnLoader(value)}
            handleEventFields={(data) => {
              dispatch(setEventFields(data))
            }}
          />
        ) : (
          <Settings />
        )}
        {showTour.modal && (
          <TourModal
            onClickHandler={() => {
              setShowTour({ guide: true, modal: false });
              setTourState((prev) => ({
                ...prev,
                run: true,
                steps: createTourSteps()
              }));
            }}
            resetModal={() => {
              setShowTour((prev) => ({ ...prev, modal: false }));
              setLocalStorageValue(SHOW_TOUR, booleanStringValues.FALSE);
            }}
          />
        )}
      </Flex>
    </>
  );
};

export default CodeEditor;

const TourModal = ({
  resetModal,
  onClickHandler
}: {
  onClickHandler: () => void;
  resetModal: () => void;
}) => {
  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: '$6',
          borderRadius: '$lg',
          background: '#f3f9fa',
          width: 496,
          boxShadow: '0px 4px 28px 0px rgba(104, 184, 194, 1)'
        }}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          css={{ textAlign: 'center', paddingInline: '$12', paddingTop: '$12' }}
        >
          <RocketIcon size={56} color="#56B0BB" />
          <Heading size="h5" weight="medium" css={{ paddingBlock: '$6 $2' }}>
            Welcome to{' '}
            <Box as="span" css={{ color: '$primary400' }}>
              AppNest Codespace
            </Box>
          </Heading>
          <Text size="sm">
            To ensure a seamless experience, we encourage you to take a small
            tour on AppNest Codespace
          </Text>
        </Flex>
        <Flex
          justifyContent="center"
          alignItems="center"
          gap="$4"
          css={{ paddingBlock: '$12 $20' }}
        >
          <Button
            onClick={() => resetModal()}
            size="lg"
            variant="ghost"
            rightIcon={<LogOut />}
          >
            Skip Tour
          </Button>
          <Button
            rightIcon={<ChevronRightIcon />}
            size="lg"
            onClick={() => onClickHandler()}
          >
            Start Tour
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};
